import { LocateInjectionCsvRowSchema } from './validation';
import Papa from 'papaparse';

export interface ParsedLocateInjectionCsvRow {
    rowIndex: number;
    symbol: string;
    quantity: number;
    pricePerShare: number;
}

export interface LocateInjectionCsvParseError {
    /** One-based data-row number; zero identifies a header/file error. */
    rowIndex: number;
    message: string;
}

export interface LocateInjectionCsvParseResult {
    rows: ParsedLocateInjectionCsvRow[];
    errors: LocateInjectionCsvParseError[];
}

const REQUIRED_HEADERS = ['symbol', 'quantity', 'price'] as const;

function normalizeHeader(value: string): string {
    return value
        .replace(/^\uFEFF/, '')
        .trim()
        .toLocaleLowerCase()
        .replace(/\s+/g, ' ');
}

function issueMessage(field: PropertyKey | undefined, message: string): string {
    const label =
        field === 'symbol' ? 'Symbol' : field === 'quantity' ? 'Quantity' : field === 'price' ? 'Price' : 'Row';
    return `${label}: ${message}`;
}

export function parseLocateInjectionCsv(text: string): LocateInjectionCsvParseResult {
    const result: LocateInjectionCsvParseResult = { rows: [], errors: [] };
    const parsed = Papa.parse<Record<string, string>>(text.replace(/^\uFEFF/, ''), {
        header: true,
        skipEmptyLines: 'greedy',
        transformHeader: normalizeHeader,
    });

    const headers = new Set(parsed.meta.fields ?? []);
    for (const header of REQUIRED_HEADERS) {
        if (!headers.has(header)) {
            const label = header.charAt(0).toLocaleUpperCase() + header.slice(1);
            result.errors.push({ rowIndex: 0, message: `Missing required column "${label}".` });
        }
    }
    if (result.errors.length > 0) return result;

    for (const error of parsed.errors) {
        result.errors.push({
            rowIndex: (error.row ?? -1) + 1,
            message: error.message,
        });
    }

    parsed.data.forEach((row, index) => {
        const rowIndex = index + 1;
        const validation = LocateInjectionCsvRowSchema.safeParse({
            symbol: row.symbol ?? '',
            quantity: row.quantity ?? '',
            price: row.price ?? '',
        });

        if (!validation.success) {
            for (const issue of validation.error.issues) {
                result.errors.push({
                    rowIndex,
                    message: `Row ${rowIndex}: ${issueMessage(issue.path[0], issue.message)}`,
                });
            }
            return;
        }

        result.rows.push({ rowIndex, ...validation.data });
    });

    if (parsed.data.length === 0 && result.errors.length === 0) {
        result.errors.push({ rowIndex: 0, message: 'The CSV does not contain any data rows.' });
    }

    return result;
}

export const LOCATE_INJECTION_CSV_TEMPLATE = 'Symbol,Quantity,Price\nAAPL,100,0.01\n';
